<script lang="ts">
  /**
   * The side panel's status block (design spec 9.4, 9.6): a select over `open` and the three
   * stored statuses, an optional note pre-filled from the record, a Save button, then the record
   * line. Pure: no store, firebase or service import. The caller saves through `onSave` and
   * patches the store, so every state here renders from props alone.
   */
  import { untrack } from "svelte";

  import * as Select from "$ui/select";
  import { Textarea } from "$ui/textarea";

  import { formatPublishedDate } from "./publishedDate";
  import {
    isReopened,
    STATUS_LABELS,
    STATUS_UNAVAILABLE_GLYPH,
    STATUS_UNAVAILABLE_LABEL,
    type ExposedDocumentWithStatus,
    type RowStatus,
    type StatusLoadState,
  } from "./status";
  import { SETTABLE_STATUSES, STATUS_NOTE_MAX_LENGTH, type SaveStatus, type SettableStatus } from "./statusSave";

  let {
    row,
    statusState = "ready",
    publishedAt,
    onSave,
  }: {
    /** The joined row: `row.status` is its record, none when it is `open`. */
    row: ExposedDocumentWithStatus;
    /** `failed` reads "Status unavailable" and disables Save; `loading` reads as `open`. */
    statusState?: StatusLoadState;
    /** D2 of the reopened line: the provider's publish time, only when its metadata reads `published`. */
    publishedAt?: Date;
    onSave: SaveStatus;
  } = $props();

  const SWEPT_COPY =
    "The latest publish no longer lists this document, so it has no status to set. Reload to see the current list.";
  const FAILED_COPY = "The status could not be saved. Try again.";

  const record = $derived(row.status);
  const reopened = $derived(isReopened(row, record));
  const unavailable = $derived(statusState === "failed");

  // The pick Save would store: never `reopened`, which is derived, not stored. A reopened row
  // seeds its stored status (`resolved`) but reads Reopened, with Save disabled, until a pick.
  let draft = $state<SettableStatus>("open");
  let awaitingPick = $state(false);
  let note = $state("");
  let saving = $state(false);
  let error = $state<"failed" | "swept" | null>(null);

  // Re-seed whenever the panel moves to another row or the row's record content changes (a
  // successful save patches the store, which lands here). Keyed on content, not identity: the
  // container rebuilds row objects on every status read or page, and an equal record must not
  // wipe an unsaved pick, note or save error.
  const seedKey = $derived(JSON.stringify([row.id, record ?? null, reopened]));
  $effect(() => {
    void seedKey;
    untrack(() => {
      draft = record?.status ?? "open";
      awaitingPick = reopened;
      note = record?.note ?? "";
      error = null;
    });
  });

  const labelOf = (value: RowStatus) => STATUS_LABELS[value];
  const onDate = (date: Date | undefined) => {
    const formatted = formatPublishedDate(date);
    return formatted ? ` on ${formatted}` : "";
  };
  const byUser = (updatedBy: string) => (updatedBy ? ` by ${updatedBy}` : "");

  const recordLine = $derived.by(() => {
    if (!record) return undefined;
    const setLine = `${STATUS_LABELS[record.status]}${byUser(record.updatedBy)}${onDate(record.updatedAt)}`;
    if (!reopened) return setLine;
    const laterPublishDate = formatPublishedDate(publishedAt);
    return laterPublishDate
      ? `${setLine}. Still exposed after the publish on ${laterPublishDate}.`
      : `${setLine}. Still exposed after a later publish.`;
  });

  /** What the select reads and ticks. */
  const shown = $derived<RowStatus>(awaitingPick ? "reopened" : draft);
  const canSave = $derived(!awaitingPick && !saving && !unavailable);

  const save = async () => {
    saving = true;
    error = null;
    try {
      const outcome = await onSave(draft, note);
      if (outcome.kind !== "saved") error = outcome.kind;
    } catch {
      error = "failed";
    } finally {
      saving = false;
    }
  };
</script>

<div class="flex flex-col gap-2" data-testid="exposure-status-block">
  <div class="min-w-[8rem] max-w-[220px]" data-testid="exposure-status-select">
    <!-- A failed read asserts no status (spec 9.3): disabled, with a neutral dash.
         Uncontrolled like the app's other selects: `shown` drives the label (placeholder) and
         the tick (`isSelected`). This single select keeps bits-ui's own pick, and its
         ItemIndicator tick would stay on that pick when the draft is re-seeded (another row, or
         a saved record) without a pick; re-mount on every re-seed to drop it. (The status
         filter's controlled multi-select does follow its `selected` prop; see
         exposedSensitiveDocuments.statusSelect.render.test.ts.) -->
    {#key seedKey}
      <Select.Root
        disabled={unavailable}
        onSelectedChange={(selected) => {
          if (selected?.value && selected.value !== "reopened") {
            draft = selected.value as SettableStatus;
            awaitingPick = false;
          }
        }}
      >
        <Select.Trigger class="h-8 text-xs" aria-label="Set status">
          <Select.Value placeholder={unavailable ? STATUS_UNAVAILABLE_GLYPH : labelOf(shown)} />
        </Select.Trigger>
        <Select.Content sameWidth>
          <Select.Group>
            {#if reopened}
              <Select.Item
                value="reopened"
                label={labelOf("reopened")}
                isSelected={shown === "reopened"}
                disabled
                class="text-xs"
              >
                {labelOf("reopened")}
              </Select.Item>
            {/if}
            {#each SETTABLE_STATUSES as status (status)}
              <Select.Item value={status} label={labelOf(status)} isSelected={shown === status} class="text-xs">
                {labelOf(status)}
              </Select.Item>
            {/each}
          </Select.Group>
        </Select.Content>
      </Select.Root>
    {/key}
  </div>
  <Textarea
    bind:value={note}
    rows={2}
    maxlength={STATUS_NOTE_MAX_LENGTH}
    placeholder="Add a note (optional)"
    aria-label="Status note"
    class="min-h-[56px] text-xs"
    data-testid="exposure-status-note"
  />
  <!-- Sized and styled like the panel's enabled "Contact document author" (#2223). -->
  <button
    type="button"
    class="inline-flex w-fit items-center gap-1 rounded bg-primary px-2 py-1 text-xs font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
    disabled={!canSave}
    onclick={save}
    data-testid="exposure-status-save"
  >
    {saving ? "Saving..." : "Save"}
  </button>
  {#if error}
    <p class="text-xs text-red-700" role="alert" data-testid="exposure-status-error">
      {error === "swept" ? SWEPT_COPY : FAILED_COPY}
    </p>
  {/if}
  {#if unavailable}
    <p class="text-[11px] text-gray-500" data-testid="exposure-status-unavailable">{STATUS_UNAVAILABLE_LABEL}</p>
  {:else if recordLine}
    <p class="text-[11px] text-gray-500" data-testid="exposure-status-record-line">{recordLine}</p>
  {/if}
</div>
